import { API_BASE_URL, resolvePublicMediaUrl } from '../config/api';

/**
 * @typedef {{
 *   id: string,
 *   fullName: string,
 *   profilePictureUrl: string | null,
 *   role: string | null,
 *   isLeader: boolean,
 *   memberType: number,
 *   parentMemberId: string | null,
 * }} CmsProjectGroupMember
 */

/**
 * @typedef {{
 *   id: string,
 *   name: string,
 *   coverImageUrl: string,
 *   groupMembers: CmsProjectGroupMember[],
 *   status: number,
 *   order: number,
 *   isActive: boolean,
 *   createdAtUtc: string,
 * }} CmsProjectItem
 */

/**
 * @param {CmsProjectItem} project
 */
function buildProjectDescription(project) {
    const members = Array.isArray(project.groupMembers)
        ? project.groupMembers
        : [];
    const leader = members.find((m) => m.isLeader);
    const parts = [];
    if (leader?.fullName) {
        parts.push(`Lider: ${leader.fullName}`);
    }
    if (members.length > 0) {
        parts.push(`${members.length} ekip üyesi`);
    }
    return (
        parts.join(' · ') ||
        'Rotary kulübümüzün toplumsal sorumluluk projesi.'
    );
}

/**
 * ProjectCard / projeler listesi satırı.
 * @param {CmsProjectItem} project
 * @returns {{ id: string, imageUrl: string, title: string, description: string, href: string }}
 */
export function mapCmsProjectToCardItem(project) {
    return {
        id: project.id,
        imageUrl: resolvePublicMediaUrl(project.coverImageUrl),
        title: project.name,
        description: buildProjectDescription(project),
        href: `/projects/${encodeURIComponent(project.id)}`,
    };
}

/**
 * Proje detayı (GetById).
 * @typedef {{
 *   id: string,
 *   name: string,
 *   description: string,
 *   groupMembers: CmsProjectGroupMember[],
 *   startDate: string | null,
 *   endDate: string | null,
 *   theme: string | null,
 *   purpose: string | null,
 *   coverImageUrl: string | null,
 *   gallery: unknown[],
 *   status: number,
 *   order: number,
 *   isActive: boolean,
 *   createdAtUtc: string,
 *   updatedAtUtc?: string,
 * }} CmsProjectDetail
 */

/**
 * @param {string} id
 * @returns {Promise<CmsProjectDetail>}
 */
export async function fetchCmsProjectById(id) {
    const rawId = String(id || '').trim();
    if (!rawId) {
        throw new Error('Proje kimliği gerekli');
    }
    const params = new URLSearchParams({ id: rawId });
    const url = `${API_BASE_URL}/cms/Project/GetById?${params}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Proje detayı okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Proje detayı alınamadı (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Proje bulunamadı';
        throw new Error(msg);
    }

    const data = /** @type {{ data?: unknown }} */ (obj).data;
    if (!data || typeof data !== 'object') {
        throw new Error('Proje bulunamadı');
    }

    return /** @type {CmsProjectDetail} */ (data);
}

/**
 * @param {CmsProjectDetail} project
 * @returns {string[]}
 */
export function getProjectGalleryUrls(project) {
    const g = Array.isArray(project.gallery) ? project.gallery : [];
    /** @type {string[]} */
    const urls = [];
    for (const item of g) {
        if (!item || typeof item !== 'object') continue;
        const raw =
            /** @type {{ url?: string, imageUrl?: string }} */ (item).url ||
            /** @type {{ imageUrl?: string }} */ (item).imageUrl;
        if (typeof raw === 'string' && raw.trim()) {
            urls.push(resolvePublicMediaUrl(raw.trim()));
        }
    }
    return [...new Set(urls)];
}

/**
 * @param {{ pageNumber?: number, pageSize?: number, accessToken?: string | null }} [opts]
 * @returns {Promise<CmsProjectItem[]>}
 */
export async function fetchCmsProjectsList(opts = {}) {
    const pageNumber = opts.pageNumber ?? 1;
    const pageSize = opts.pageSize ?? 10;
    const params = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
    });
    const url = `${API_BASE_URL}/cms/Project/GetAll?${params}`;

    /** @type {Record<string, string>} */
    const headers = {
        Accept: 'application/json',
    };
    if (opts.accessToken) {
        headers.Authorization = `Bearer ${opts.accessToken}`;
    }

    const res = await fetch(url, {
        method: 'GET',
        headers,
    });

    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('Proje listesi okunamadı');
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `Proje listesi alınamadı (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : 'Proje listesi alınamadı';
        throw new Error(msg);
    }

    const raw =
        /** @type {{ data?: unknown, Data?: unknown }} */ (obj).data ??
        /** @type {{ Data?: unknown }} */ (obj).Data;
    const list = Array.isArray(raw) ? raw : [];

    return /** @type {CmsProjectItem[]} */ (list)
        .filter(
            (x) =>
                x &&
                typeof x === 'object' &&
                /** @type {{ isActive?: boolean }} */ (x).isActive === true,
        )
        .sort(
            (a, b) =>
                (Number(a.order) || 0) - (Number(b.order) || 0),
        );
}
